import { Box, colors, Flex } from "@biblioteksentralen/react";
import { FlexPropsWithoutAnimation, useTransitionProps } from "../utils";
import { BowTie } from "./BowTie";
import { useReducedMotion } from "framer-motion";

export type ChristmasPresentColor = { wrappingColor: string; ribbonColor: string; dotsColor: string };

type EightItems<T> = [T, T, T, T, T, T, T, T];

export const christmasPresentColors: EightItems<ChristmasPresentColor> = [
  // gray
  { wrappingColor: colors.grey10, ribbonColor: colors.grey30, dotsColor: colors.grey30 },
  // green and peach
  { wrappingColor: colors.accentGreen, ribbonColor: colors.accentPeach, dotsColor: colors.accentPeach },
  // peach and red
  { wrappingColor: colors.accentPeach, ribbonColor: colors.bsRødDark, dotsColor: colors.statusGreenLight },
  // dark yellow and peach
  { wrappingColor: colors.statusYellow, ribbonColor: colors.accentPeach, dotsColor: colors.lightPeach },
  // blue and dark green
  { wrappingColor: colors.accentBlueMedium, ribbonColor: colors.accentGreen, dotsColor: colors.statusGreenLight },
  // yellow and dark green
  { wrappingColor: colors.accentYellow, ribbonColor: colors.accentGreen, dotsColor: colors.lightPeach },
  // dark purple and yellow
  { wrappingColor: colors.accentPurple, ribbonColor: colors.accentYellow, dotsColor: colors.lightPeach },
  // green and yellow
  { wrappingColor: colors.statusGreen, ribbonColor: colors.accentYellow, dotsColor: colors.lightPurple },
];

const dotPositions: { left?: string; bottom?: string; right?: string; top?: string }[] = [
  { left: "7%", bottom: "15%" },
  { left: "20%", bottom: "30%" },
  { left: "5%", top: "35%" },
  { left: "23%", top: "31%" },
  { left: "14%", top: "7%" },
  { right: "5%", bottom: "11%" },
  { right: "26%", bottom: "25%" },
  { right: "11%", bottom: "46%" },
  { right: "21%", top: "23%" },
  { right: "6%", top: "11%" },
];

const undulationLength = "0.17rem";

// We want to animate the 'transform' prop, without affecting existing additional transform
// settings. Therefore we add to the existing.
const getUndulateFrames = (existingTransform: any = "") => ({
  "@keyframes": {
    "0%": { transform: `translateY(0) ${existingTransform}` },
    "25%": { transform: `translateY(-${undulationLength}) ${existingTransform}` },
    "50%": { transform: `translateY(0) ${existingTransform}` },
    "75%": { transform: `translateY(${undulationLength}) ${existingTransform}` },
    "100%": { transform: `translateY(0) ${existingTransform}` },
  },
  animationKey: "undulate",
});

type Keyframes = {
  "@keyframes": { [key: string]: { transform: string } };
  animationKey: string;
};

// We control the animation using time-based animation-play-state rather than
// animation-iteration-count, to avoid jittering when turning animation on and off.
const createAnimation = (playState: string, keyframes: Keyframes, duration: string) => {
  const animationKey = `@keyframes ${keyframes["animationKey"]}`;
  return {
    [animationKey]: keyframes["@keyframes"],
    animation: `${keyframes.animationKey} ${duration} linear infinite ${playState}`,
  };
};

// Not all animation props can be set in Chakra, and some of them we handle specially
type AnimationProps = {
  animateOnHover?: boolean;
  animationDelay?: string;
  animationPlayState?: string;
  animationKeyFrames?: Keyframes;
  animationDuration?: string;
};

type Props = ChristmasPresentColor & AnimationProps & FlexPropsWithoutAnimation;

export const ChristmasPresent = ({
  animationPlayState = "paused",
  animationDuration = "1.5s",
  animationDelay = "0s",
  animationKeyFrames,
  width,
  height,
  wrappingColor,
  ribbonColor,
  dotsColor,
  children,
  transitionDelay,
  transform,
  animateOnHover,
  onClick,
  ...flexProps
}: Props) => {
  const prefersReducedMotion = useReducedMotion();
  const transitionProps = useTransitionProps();

  const animation = prefersReducedMotion
    ? undefined
    : createAnimation(animationPlayState, animationKeyFrames ?? getUndulateFrames(transform), animationDuration);

  const animationOnHover = animateOnHover && !prefersReducedMotion ? { animationPlayState: "running" } : {};

  return (
    <Flex
      position="relative"
      width={width}
      height={height}
      borderRadius="9%"
      justifyContent="center"
      background={wrappingColor}
      transform={transform}
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
      _hover={{ base: {}, md: animationOnHover }}
      // Chakra does not accept animation-delay
      css={{ animationDelay: animationDelay, ...animation }}
      transitionDelay={prefersReducedMotion ? undefined : transitionDelay}
      {...transitionProps}
      {...flexProps}
    >
      <BowTie placement="left" fill={ribbonColor} transitionDelay={transitionDelay} />
      <BowTie placement="right" fill={ribbonColor} transitionDelay={transitionDelay} />
      <Box width="25%" height="100%" background={ribbonColor} transitionDelay={transitionDelay} {...transitionProps} />
      {dotPositions.map(({ left, bottom, right, top }, idx) => (
        <Box
          key={idx}
          background={dotsColor}
          borderRadius="50%"
          position="absolute"
          width="6%"
          height="6%"
          left={left}
          bottom={bottom}
          right={right}
          top={top}
          transitionDelay={transitionDelay}
          {...transitionProps}
        />
      ))}
      <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={1}>
        {children}
      </Box>
    </Flex>
  );
};
